import { FC, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import moment from 'moment'
import { CustomCalendar } from 'src/App/AppRouter/Layout/TopBar/TopBarDatePicker/CustomCalendar'
import { CalendarContext } from 'src/utils/calendarContext'
import styles from './timeFilterModal.module.scss'

interface TimeFilterModalProps {
  calendar?: CalendarContext
  onClose: () => void
  onGenerate?: (from: moment.Moment, to: moment.Moment) => void
}

type DateField = 'from' | 'to' | null

const CalendarIcon: FC = () => (
  <svg
    fill="none"
    height="16"
    viewBox="0 0 24 24"
    width="16"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      height="16"
      rx="3"
      stroke="#25262D"
      strokeOpacity="0.4"
      strokeWidth="1.5"
      width="18"
      x="3"
      y="4"
    />
    <path
      d="M3 9h18M8 2v4M16 2v4"
      stroke="#25262D"
      strokeLinecap="round"
      strokeOpacity="0.4"
      strokeWidth="1.5"
    />
  </svg>
)

const CloseIcon: FC = () => (
  <svg fill="none" height="12" viewBox="0 0 12 12" width="12">
    <path
      d="M1 1l10 10M11 1L1 11"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="1.5"
    />
  </svg>
)

const WarningIcon: FC = () => (
  <svg fill="none" height="16" viewBox="0 0 16 16" width="16">
    <path
      d="M8 6v3M8 11h.01M6.86 2.57 1.22 12a1.33 1.33 0 0 0 1.14 2h11.28a1.33
      1.33 0 0 0 1.14-2L9.14 2.57a1.33 1.33 0 0 0-2.28 0Z"
      stroke="#FF6833"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
)

export const TimeFilterModal: FC<TimeFilterModalProps> = ({
  calendar,
  onClose,
  onGenerate,
}) => {
  const [fromDate, setFromDate] = useState<moment.Moment | null>(null)
  const [toDate, setToDate] = useState<moment.Moment | null>(null)
  const [openCalendar, setOpenCalendar] = useState<DateField>(null)

  // lock body scroll while modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  const formatDate = (d: moment.Moment | null) =>
    d ? d.format('MMM D, YYYY') : ''

  const handleCalendarApply = (start: moment.Moment, end: moment.Moment) => {
    if (openCalendar === 'from') setFromDate(start)
    else setToDate(end)
    setOpenCalendar(null)
  }

  const dateRangeLabel =
    fromDate && toDate
      ? `Between ${fromDate.format('MMM D, YYYY')} - ${toDate.format(
          'MMM D, YYYY'
        )}`
      : fromDate
        ? `From ${fromDate.format('MMM D, YYYY')}`
        : toDate
          ? `To ${toDate.format('MMM D, YYYY')}`
          : null

  const removeDate = () => {
    setFromDate(null)
    setToDate(null)
  }

  const handleGenerate = () => {
    if (fromDate && toDate) onGenerate?.(fromDate, toDate)
  }

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
      >
        <div className={styles.header}>
          <h2 className={styles.title}>Time Filter</h2>
          <button
            className={styles.closeButton}
            onClick={onClose}
            type="button"
          >
            <CloseIcon />
          </button>
        </div>

        <div className={styles.content}>
          <h3 className={styles.contentTitle}>Generate New Summary</h3>
          <p className={styles.contentHint}>
            Select a date range to generate a summary for that period.
          </p>

          <div className={styles.dateRow}>
            <div className={styles.dateGroup}>
              <span className={styles.dateLabel}>Date Range</span>
              <button
                className={`${styles.dateInput} ${
                  openCalendar === 'from' ? styles.dateInputActive : ''
                }`}
                onClick={() =>
                  setOpenCalendar(openCalendar === 'from' ? null : 'from')
                }
                type="button"
              >
                <CalendarIcon />
                <span className={styles.dateInputText}>
                  {formatDate(fromDate) || 'Start date'}
                </span>
              </button>
              {openCalendar === 'from' && (
                <div className={styles.inlineCalendar}>
                  <CustomCalendar
                    {...calendar}
                    initialEnd={fromDate}
                    initialStart={fromDate}
                    maxDate={toDate}
                    onApply={handleCalendarApply}
                    onClose={() => setOpenCalendar(null)}
                    onReset={() => setFromDate(null)}
                    selectionMode="single"
                  />
                </div>
              )}
            </div>

            <div className={styles.dateGroup}>
              <span className={styles.dateLabel}>&nbsp;</span>
              <button
                className={`${styles.dateInput} ${
                  openCalendar === 'to' ? styles.dateInputActive : ''
                }`}
                onClick={() =>
                  setOpenCalendar(openCalendar === 'to' ? null : 'to')
                }
                type="button"
              >
                <CalendarIcon />
                <span className={styles.dateInputText}>
                  {formatDate(toDate) || 'End date'}
                </span>
              </button>
              {openCalendar === 'to' && (
                <div className={styles.inlineCalendar}>
                  <CustomCalendar
                    {...calendar}
                    initialEnd={toDate}
                    initialStart={toDate}
                    minDate={fromDate}
                    onApply={handleCalendarApply}
                    onClose={() => setOpenCalendar(null)}
                    onReset={() => setToDate(null)}
                    selectionMode="single"
                  />
                </div>
              )}
            </div>
          </div>

          {dateRangeLabel && (
            <div className={styles.chip}>
              <span className={styles.chipLabel}>
                <span className={styles.chipKey}>Date: </span>
                {dateRangeLabel}
              </span>
              <button
                className={styles.chipRemove}
                onClick={removeDate}
                type="button"
              >
                <CloseIcon />
              </button>
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <div className={styles.footerNote}>
            <WarningIcon />
            <span className={styles.footerNoteText}>
              {/* Backed by sm.back periodSummary: one summary per
                  analysis / campaign / facility per rolling 24 hours; the
                  widget shows the countdown. */}
              You can generate one summary per day.
            </span>
          </div>
          <div className={styles.footerActions}>
            <button
              className={styles.cancelButton}
              onClick={onClose}
              type="button"
            >
              Cancel
            </button>
            <button
              className={styles.generateButton}
              disabled={!fromDate || !toDate}
              onClick={handleGenerate}
              type="button"
            >
              Generate Summary
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
